import React, { useState, useEffect, useMemo } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import './MedalPage.css';

interface MedalPageProps {
  position: 'bronze' | 'silver' | 'gold';
}

interface PodiumConfig {
  title: string;
  subtitle: string;
  teams: {
    first?: string;
    second?: string;
    third?: string;
  };
  teamImages: {
    [teamName: string]: string;
  };
  showPodium: boolean;
  currentSlide: number;
}

const MedalPage: React.FC<MedalPageProps> = ({ position }) => {
  const [config, setConfig] = useState<PodiumConfig>({
    title: 'Podium de Ganadores',
    subtitle: 'Neurolimpiadas 2025',
    teams: {},
    teamImages: {},
    showPodium: false,
    currentSlide: 0
  });
  // Estados para precarga y transiciones
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting] = useState(false);

  // Mapeo de posiciones
  const positionMap = {
    bronze: { rank: 3, teamKey: 'third' as const, medalName: 'Bronce', color: '#CD7F32', cssClass: 'bronze' },
    silver: { rank: 2, teamKey: 'second' as const, medalName: 'Plata', color: '#C0C0C0', cssClass: 'silver' },
    gold: { rank: 1, teamKey: 'first' as const, medalName: 'Oro', color: '#FFD700', cssClass: 'gold' }
  };
  const currentPosition = positionMap[position];

  // Lista de imágenes a precargar específicas para medallas
  const imagesToPreload = useMemo(() => [
    '/img/teams/el-rosario.png',
    '/img/teams/guimar.png',
    '/img/teams/la-victoria.png',
    '/img/teams/santa-ursula.png',
    '/img/neuromajo.png',
    '/img/neurolimpiadas_back.svg'
  ], []);

  // Precargar imágenes al montar el componente
  useEffect(() => {
    const preloadImages = () => {
      console.log(`🏅 [MedalPage-${position}] Iniciando precarga de imágenes...`);
      const imagePromises = imagesToPreload.map((src) => {
        return new Promise<string>((resolve) => {
          const img = new Image();
          img.onload = () => {
            console.log(`✅ [MedalPage-${position}] Imagen cargada: ${src}`);
            setLoadedImages(prev => new Set([...Array.from(prev), src]));
            resolve(src);
          };
          img.onerror = () => {
            console.warn(`⚠️ [MedalPage-${position}] Error cargando imagen: ${src}`);
            resolve(src); // Resolver de todos modos para no bloquear
          };
          img.src = src;
        });
      });

      Promise.all(imagePromises).then(() => {
        console.log(`🎉 [MedalPage-${position}] Todas las imágenes han sido precargadas`);
        setImagesLoaded(true);
        // Activar animación de entrada después de un pequeño delay
        setTimeout(() => setIsVisible(true), 100);
      });
    };

    preloadImages();
  }, [position, imagesToPreload]);  // Cargar configuración inicial
  useEffect(() => {
    const savedConfig = localStorageManager.getConfig();
    if (savedConfig.podium) {
      setConfig(prev => ({
        ...prev,
        ...savedConfig.podium
      }));
    }
  }, []);

  // Event listeners
  useEffect(() => {
    const handleConfigUpdate = (data: any) => {
      if (data.section === 'podium') {
        setConfig(prev => ({ ...prev, ...data.data }));
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);

    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
    };
  }, []);

  // Mapeo de equipos a municipios
  const getTeamMunicipality = (teamName: string): string => {
    const teamToMunicipality: { [key: string]: string } = {
      // Güímar
      'Chinguaro': 'Güímar',
      'Acaymo': 'Güímar',
      'Arena': 'Güímar',
      'Costeras': 'Güímar',
      
      // La Victoria
      'Las Guays': 'La Victoria',
      'Reinas valientes': 'La Victoria',
      'Arroyadoras': 'La Victoria',
      'Tagoro': 'La Victoria',
      
      // Santa Úrsula
      'Piña': 'Santa Úrsula',
      'Corujera': 'Santa Úrsula',
      'Guayota': 'Santa Úrsula',
      'Yeyes': 'Santa Úrsula',
      'Los Cipreses': 'Santa Úrsula',
      
      // El Rosario
      'Lomo Pelado': 'El Rosario',
      'Noveleras': 'El Rosario',
      'Poderosas': 'El Rosario',
      'Perlas': 'El Rosario',
      'Las Élites': 'El Rosario',
      'Madroño': 'El Rosario'
    };
    
    return teamToMunicipality[teamName] || teamName;
  };

  const getTeamImage = (teamName: string): string => {
    if (!teamName) return '';
    
    const municipality = getTeamMunicipality(teamName);
    
    const municipalityImageMap: { [key: string]: string } = {
      'El Rosario': '/img/teams/el-rosario.png',
      'Güímar': '/img/teams/guimar.png', 
      'La Victoria': '/img/teams/la-victoria.png',
      'Santa Úrsula': '/img/teams/santa-ursula.png'
    };

    return municipalityImageMap[municipality] || '';
  };

  const teamName = config.teams[currentPosition.teamKey];
  const teamImage = getTeamImage(teamName || '');  return (
    <div className={`medal-page-fullscreen ${isVisible ? 'visible' : ''} ${isExiting ? 'exiting' : ''}`}>
      {/* Indicador de carga de imágenes */}
      {!imagesLoaded && (
        <div className="medal-loading-overlay">
          <div className="medal-loading-content">
            <div className="medal-loading-spinner">🏅</div>
            <p>Cargando medalla de {currentPosition.medalName}...</p>
            <div className="medal-loading-progress">
              {loadedImages.size} / {imagesToPreload.length} recursos
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div className={`medal-content ${imagesLoaded ? 'loaded' : ''}`}>
        {/* Información del equipo */}
        <div className="team-info-medal">
          <div className="position-info">
            <span className="position-number" style={{ color: currentPosition.color }}>
              {currentPosition.rank}°
            </span>
          </div>
          
          {teamName ? (
            <>
              {teamImage && (
                <div className="team-image-container">
                  <img 
                    src={teamImage} 
                    alt={`Equipo ${teamName}`}
                    className="team-image-medal"
                    style={{ opacity: imagesLoaded ? 1 : 0 }}
                  />
                </div>
              )}
              <div 
                className={`team-name-medal ${currentPosition.cssClass}`} 
                style={{ 
                  fontSize: '5rem',
                  fontWeight: 'bold'
                }}
              >
                {teamName}
              </div>
{/*               <div className="team-municipality">{getTeamMunicipality(teamName)}</div>
 */}            </>
          ) : (
            <div className="waiting-team">
              <span>Equipo por determinar</span>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default MedalPage;
